import * as React from 'react';

import { translate } from '~l10n/client';
import { groupBy } from 'underscore';

import { UIButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { Inline } from '~shared/components/Inline';
import { toast } from '~shared/components/Toast';
import { useQuizzes } from '~teacher/hook';
import { useMoveQuizFolder } from '~teacher/hook/useMoveQuizFolder';

import { QuizItemCache } from '../../../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { matchUniqueIdToEntityId } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';
import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';
import { QuizTraverseList } from './QuizTraverseList';

const useSelectQuizFolder = (itemIds: Set<string>) =>
  React.useCallback(
    (quizItemCache: QuizItemCache) =>
      [...itemIds]
        .map((itemId): QuizOrFolder => quizItemCache.itemById[itemId])
        .filter(Boolean),
    [itemIds]
  );

function getQuizTitle(itemsToMove: QuizOrFolder[]): string {
  if (!itemsToMove?.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('Move {0}').format(itemToMove.name);
  }

  return translate('Move {0} items').format(itemsToMove.length);
}

function getSuccessToastMessage(itemsToMove: QuizOrFolder[]) {
  if (!itemsToMove.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('{0} moved successfully').format(itemToMove.name);
  }

  return translate('{0} items moved successfully').format(itemsToMove.length);
}

function useOnMoveItems(
  itemsToMove: QuizOrFolder[],
  onClose: (success: boolean, newParentId: number) => void,
  isOpen: boolean
) {
  const [isMoveDisabled, setIsMoveDisabled] = React.useState(false);

  const [selectedParentId, setSelectedParentId] = React.useState<number>(null);
  const initialParentId = itemsToMove?.[0]?.parentId;

  // Whenever the dialog is opened, reset the selected parent id to parent id of the first item
  React.useEffect(() => {
    if (
      (isOpen && initialParentId === null) ||
      typeof initialParentId === 'number'
    ) {
      setSelectedParentId(initialParentId);
    }
  }, [isOpen, initialParentId]);

  const handleSelectedFolder = React.useCallback(
    (itemId: string) => {
      let newParentId = null;
      if (itemId !== null) {
        const { entityId, itemType } = matchUniqueIdToEntityId(itemId);
        if (itemType === 'folder') {
          newParentId = entityId;
        }
      }

      setSelectedParentId(newParentId);
      if (itemsToMove) {
        let shouldDisable = false;
        for (const item of itemsToMove) {
          if (item.type === 'folder' && newParentId === item.id) {
            console.error('Cannot move a folder into itself');
            shouldDisable = true;
            break;
          }
        }
        setIsMoveDisabled(
          shouldDisable || itemsToMove[0].parentId === newParentId
        );
      }
    },
    [setSelectedParentId, itemsToMove]
  );

  const moveMutation = useMoveQuizFolder();
  const onMove = React.useCallback(() => {
    if (!itemsToMove) return;
    setIsMoveDisabled(true);
    const { quiz, folder } = groupBy(
      itemsToMove,
      (quizOrFolder) => quizOrFolder.type
    );

    const folderIds = (folder ?? []).map((folder) => folder.id);
    const quizIds = (quiz ?? []).map((quiz) => quiz.id);
    moveMutation.mutate(
      {
        quizIds: quizIds,
        folderIds: folderIds,
        parentId: selectedParentId,
      },
      {
        onSuccess: () => {
          toast(getSuccessToastMessage(itemsToMove), { tone: 'positive' });
          onClose(true, selectedParentId);
        },
        onError: (error) => {
          if (error instanceof Error) {
            toast(`Error: ${error.message}`, { tone: 'critical' });
          }
          setIsMoveDisabled(false);
        },
      }
    );
  }, [itemsToMove, selectedParentId, moveMutation, onClose]);

  return {
    moveMutation,
    onMove,
    selectedParentId,
    isMoveDisabled,
    handleSelectedFolder,
  };
}

export interface Props {
  itemIdsToMove: Set<string>;
  onClose: (success: boolean, newParentFolder?: number) => void;
  isOpen: boolean;
}

/**
 * @deprecated use MoveDialog instead
 */
export function MoveQuizFolderDialog({
  itemIdsToMove,
  onClose,
  isOpen,
}: Props) {
  const select = useSelectQuizFolder(itemIdsToMove);
  const enabled = itemIdsToMove?.size >= 1;

  const itemsToMove = useQuizzes({
    select: select,
    enabled: enabled,
  });

  const quizTitle = React.useMemo(
    () => getQuizTitle(itemsToMove.data),
    [itemsToMove.data]
  );

  const { onMove, isMoveDisabled, handleSelectedFolder, selectedParentId } =
    useOnMoveItems(itemsToMove.data, onClose, isOpen);

  return (
    <Dialog
      onClose={() => onClose(false)}
      id="move-quiz-dialog"
      open={isOpen}
      title={quizTitle}
      closeLabel={translate('Close')}
    >
      <div className="mt-5 mb-5" />
      <div className="text-base leading-8">
        <QuizTraverseList
          selectedParentFolder={selectedParentId}
          onSelected={handleSelectedFolder}
          selectedItems={itemsToMove.isFetched ? itemsToMove.data : []}
        />
      </div>
      <div>
        <Divider />
        <div className="mt-8 pt-8">
          <Inline align="right">
            <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
            <UIButton
              appearance="primary"
              onClick={onMove}
              isDisabled={isMoveDisabled}
            >
              {translate('Move')}
            </UIButton>
          </Inline>
        </div>
      </div>
    </Dialog>
  );
}
